/**
 * Vitamin concept-test segments service.
 *
 * Fetches the segment / sub-segment breakdown for a population from the backend
 * proxy (`GET /vitamin/populations/{population}/segments`), which in turn proxies
 * the external population service. Used by the concept-test form to let users
 * narrow a run to specific segments / sub-segments.
 */

const API_URL = import.meta.env.VITE_API_URL || '';
const CACHE_TTL_MS = 5 * 60 * 1000;

export interface VitaminSubSegment {
  sub_segment_id: number;
  sub_segment_name: string;
  sub_segment_description?: string;
  agent_count: number;
  /** Distinction-clarity breakdown within this sub-segment (value -> agent count). */
  distinction_clarity?: Record<string, number>;
}

export interface VitaminSegment {
  segment_id: number;
  segment_name: string;
  segment_description?: string;
  agent_count: number;
  sub_segments: VitaminSubSegment[];
  /** Distinction-clarity breakdown within this segment (e.g. { clear, blurry, conflate }). */
  distinction_clarity?: Record<string, number>;
}

export interface PopulationSegments {
  population: string;
  total_agents: number;
  segments: VitaminSegment[];
  /**
   * Optional distinction-clarity breakdown (e.g. archer jerky):
   * { clear: 215, blurry: 65, conflate: 23 } — value -> agent count.
   * Present only for populations the external service tags with it.
   */
  distinction_clarity?: Record<string, number>;
}

interface CacheEntry {
  data: PopulationSegments;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<PopulationSegments>>();

function normalize(raw: unknown, population: string): PopulationSegments {
  const data = (raw || {}) as Partial<PopulationSegments>;
  const segments = Array.isArray(data.segments) ? data.segments : [];
  const clarity =
    data.distinction_clarity && typeof data.distinction_clarity === 'object'
      ? (data.distinction_clarity as Record<string, number>)
      : undefined;
  return {
    population: data.population || population,
    total_agents: typeof data.total_agents === 'number' ? data.total_agents : 0,
    segments: segments.map((s) => ({
      ...s,
      sub_segments: Array.isArray(s?.sub_segments) ? s.sub_segments : [],
    })),
    distinction_clarity: clarity,
  };
}

/**
 * Fetch segments for a population. Returns a normalized payload; a population with
 * no segments resolves to `{ segments: [] }` (caller falls back to defaults).
 * De-duplicates concurrent requests and caches results for a few minutes.
 */
export async function fetchPopulationSegments(population: string): Promise<PopulationSegments> {
  if (!population) return { population, total_agents: 0, segments: [] };

  const cached = cache.get(population);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) return cached.data;

  const existing = inFlight.get(population);
  if (existing) return existing;

  const request = (async () => {
    const resp = await fetch(
      `${API_URL}/vitamin/populations/${encodeURIComponent(population)}/segments`,
      { credentials: 'include' },
    );
    if (!resp.ok) throw new Error(`Failed to fetch segments (${resp.status})`);
    const data = normalize(await resp.json(), population);
    cache.set(population, { data, timestamp: Date.now() });
    return data;
  })();

  inFlight.set(population, request);
  try {
    return await request;
  } finally {
    inFlight.delete(population);
  }
}

export interface FilterCountBreakdown {
  segment: string;
  sub_segments?: string[];
  distinction_clarity?: string[];
  matched: number;
}

export interface FilterCountResult {
  population: string;
  total_agents: number;
  matched_agents: number;
  /** Per-segment matched counts under the current filter. */
  breakdown?: FilterCountBreakdown[];
}

/**
 * Ask the external service how many agents match the current filter (segment /
 * sub-segment / distinction-clarity). Body is the same filter shape the run uses:
 * `{ segment_filter }` or `{ segments, sub_segments, distinction_clarity }`.
 */
export async function fetchFilterCount(
  population: string,
  filter: Record<string, unknown>,
): Promise<FilterCountResult> {
  const resp = await fetch(
    `${API_URL}/vitamin/populations/${encodeURIComponent(population)}/filter-count`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filter),
    },
  );
  if (!resp.ok) throw new Error(`Failed to fetch filter count (${resp.status})`);
  return resp.json();
}
